import React, { useEffect, useState, useCallback } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid
} from "recharts";

const API = "https://vms.hexagons.tech/api";

export default function Dashboard() {
  const [visitors, setVisitors] = useState([]);

  const token = localStorage.getItem("token");

  // ============================
  // FETCH
  // ============================
  const fetchVisitors = useCallback(async () => {
    try {
      const res = await fetch(`${API}/visitors`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const data = await res.json();
      setVisitors(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  }, [token]);

  useEffect(() => {
    fetchVisitors();
  }, [fetchVisitors]);

  // ============================
  // KPIs
  // ============================
  const today = new Date().toDateString();

  const todayVisitors = visitors.filter(
    (v) => new Date(v.entryTime).toDateString() === today
  );

  const inside = visitors.filter((v) => v.status === "inside").length;
  const totalToday = todayVisitors.length;
  const exitedToday = todayVisitors.filter(
    (v) => v.status === "exited"
  ).length;

  // ============================
  // DATA CHARTS
  // ============================
  const visitsByHour = {};
  todayVisitors.forEach((v) => {
    const h = new Date(v.entryTime).getHours();
    visitsByHour[h] = (visitsByHour[h] || 0) + 1;
  });

  const chartData = Object.keys(visitsByHour).map((h) => ({
    hour: `${h}:00`,
    visits: visitsByHour[h]
  }));

  const reasonCount = {};
  todayVisitors.forEach((v) => {
    reasonCount[v.reason] = (reasonCount[v.reason] || 0) + 1;
  });

  const reasonData = Object.keys(reasonCount).map((r) => ({
    name: r,
    value: reasonCount[r]
  }));

  const alerts = visitors.filter((v) => {
    if (v.status !== "inside") return false;
    const hours =
      (new Date() - new Date(v.entryTime)) / (1000 * 60 * 60);
    return hours > 4;
  });

  return (
    <div className="p-4 md:p-6 space-y-6 bg-background min-h-screen">

      {/* HEADER */}
      <div>
        <h1 className="text-2xl font-bold text-primary">
          Dashboard
        </h1>
        <p className="text-gray-500 text-sm">
          Control en tiempo real de visitantes
        </p>
      </div>

      {/* KPI CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">

        <div className="bg-white border-l-4 border-primary p-4 rounded-xl shadow">
          <p className="text-gray-500 text-sm">Visitantes dentro</p>
          <h2 className="text-2xl font-bold text-primary">{inside}</h2>
        </div>

        <div className="bg-white border-l-4 border-accent p-4 rounded-xl shadow">
          <p className="text-gray-500 text-sm">Ingresos hoy</p>
          <h2 className="text-2xl font-bold text-accent">{totalToday}</h2>
        </div>

        <div className="bg-white border-l-4 border-secondary p-4 rounded-xl shadow">
          <p className="text-gray-500 text-sm">Salidas hoy</p>
          <h2 className="text-2xl font-bold text-secondary">{exitedToday}</h2>
        </div>

        <div className="bg-white border-l-4 border-red-500 p-4 rounded-xl shadow">
          <p className="text-gray-500 text-sm">Alertas</p>
          <h2 className="text-2xl font-bold text-red-500">{alerts.length}</h2>
        </div>

      </div>

      {/* GRID PRINCIPAL */}
      <div className="grid md:grid-cols-2 gap-6">

        {/* GRÁFICO HORAS */}
        <div className="bg-white p-5 rounded-2xl shadow">
          <h3 className="font-semibold mb-4 text-primary">
            Flujo por hora
          </h3>

          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={chartData}>
              <XAxis dataKey="hour" />
              <YAxis />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="visits"
                stroke="#1e3a8a"
                strokeWidth={3}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* MOTIVOS */}
        <div className="bg-white p-5 rounded-2xl shadow">
          <h3 className="font-semibold mb-4 text-primary">
            Motivos de visita
          </h3>

          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={reasonData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#0ea5e9" />
            </BarChart>
          </ResponsiveContainer>
        </div>

      </div>

      {/* ACTIVIDAD + ALERTAS */}
      <div className="grid md:grid-cols-2 gap-6">

        {/* ACTIVIDAD */}
        <div className="bg-white p-5 rounded-2xl shadow">
          <h3 className="font-semibold mb-4 text-primary">
            Actividad reciente
          </h3>

          <div className="space-y-2">
            {visitors.slice(0, 5).map((v) => (
              <div
                key={v._id}
                className="flex justify-between text-sm border-b pb-2"
              >
                <span className="font-medium">{v.name}</span>
                <span className="text-gray-500">
                  {new Date(v.entryTime).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ALERTAS */}
        <div className="bg-white p-5 rounded-2xl shadow">
          <h3 className="font-semibold mb-4 text-red-500">
            Alertas del sistema
          </h3>

          {alerts.length === 0 ? (
            <p className="text-gray-500 text-sm">
              No hay alertas activas
            </p>
          ) : (
            <div className="space-y-2">
              {alerts.map((v) => (
                <div
                  key={v._id}
                  className="bg-red-100 text-red-700 p-2 rounded text-sm"
                >
                  {v.name} lleva más de 4 horas dentro
                </div>
              ))}
            </div>
          )}
        </div>

      </div>

    </div>
  );
}